import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  Button,
  Form,
  Spinner,
  Alert,
  Badge,
} from "react-bootstrap";
import {
  FaPaperPlane,
  FaPaperclip,
  FaFile,
  FaTimes,
  FaLock,
} from "react-icons/fa";
import { driverAPI } from "../../api/apiSystem";
import Pusher from "pusher-js";
import { useTranslation } from "react-i18next";
import OfferMessageCard from "../chat/OfferMessageCard"; // عدّل المسار لو مختلف
import "../chat/ChatBox.css";
import RatingMessageCard from "../rating/RatingMessageCard";

Pusher.logToConsole = process.env.NODE_ENV === "development";
const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || "1a6dafa4504c853b7f61";

const parseOfferCardMessage = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("__OFFER_CARD__")) return null;
  try {
    return JSON.parse(raw.replace("__OFFER_CARD__", ""));
  } catch {
    return null;
  }
};

const parseRatingCardMessage = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("__RATING_CARD__")) return null;
  try {
    return JSON.parse(raw.replace("__RATING_CARD__", ""));
  } catch {
    return null;
  }
};

export default function DriverChatModal({
  show,
  handleClose,
  transportId,
  orderId: initialOrderId,
  // unreadCount = 0,
  onMessagesRead,
  orderStatus = null, // اختياري: accepted / rejected / pending
  order,
  unreadCount: externalUnreadCount,
}) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [attachments, setAttachments] = useState([]);
  const [isChatEnabled, setIsChatEnabled] = useState(true);
  const [resolvedOrderId, setResolvedOrderId] = useState(initialOrderId || null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const { t } = useTranslation();
  const pusherRef = useRef(null);
  const processedMessageIds = useRef(new Set());
  const currentUserRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const cleanId = (id) => {
    if (!id) return null;
    if (typeof id === "string" && id.includes("No.")) {
      return parseInt(id.replace("No.", "").trim(), 10);
    }
    return parseInt(id, 10);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const parseDate = (dateString) => {
    if (!dateString) return null;
    let str = dateString;
    const ddmmyyyy = str.match(/^(\d{2})[.-](\d{2})[.-](\d{4})\s(.*)$/);
    if (ddmmyyyy) {
      str = `${ddmmyyyy[3]}-${ddmmyyyy[2]}-${ddmmyyyy[1]}T${ddmmyyyy[4]}`;
    } else if (/^\d{4}-\d{2}-\d{2}\s/.test(str)) {
      str = str.replace(" ", "T");
    }
    const d = new Date(str);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  // const resolveOfferStatus = (offerData) => {
  //   if (orderStatus === "assigned" || orderStatus === "accepted") {
  //     return "accepted";
  //   }
  //   if (orderStatus === "rejected" || orderStatus === "cancelled") {
  //     return "rejected";
  //   }
  //   return offerData?.status || "pending";
  // };

  const resolveOfferStatus = (offerData) => {
    const status = order?.status;
    if (status === "assigned" || status === "accepted") return "accepted";
    if (status === "rejected" || status === "cancelled") return "rejected";
    return offerData?.status || "pending";
  };

  // إنشاء inquiry عند أول رسالة فقط
  const ensureOrderId = async () => {
    if (resolvedOrderId) return cleanId(resolvedOrderId);

    const cleanTransportId = cleanId(transportId);
    if (!cleanTransportId) throw new Error("Transport ID missing");

    const response = await driverAPI.transports.startInquiry(cleanTransportId);
    if (!response?.success) {
      throw new Error(response?.message || "Failed to create inquiry");
    }

    const order = response.data?.data || response.data || null;
    const newOrderId = order?.id;
    if (!newOrderId) throw new Error("Order ID missing after creating inquiry");

    setResolvedOrderId(newOrderId);
    return cleanId(newOrderId);
  };

  const markMessagesAsRead = useCallback(
    async (orderIdToUse) => {
      const cleanTransportId = cleanId(transportId);
      const cleanOrderId = cleanId(orderIdToUse || resolvedOrderId);
      if (!cleanTransportId || !cleanOrderId) return;

      try {
        const result = await driverAPI.transportOrderChat.makeRead(
          cleanTransportId,
          cleanOrderId,
        );
        if (!isMountedRef.current) return;
        if (result.success) {
          setMessages((prev) => prev.map((msg) => ({ ...msg, is_read: true })));
          if (onMessagesRead) onMessagesRead(cleanOrderId);
        }
      } catch (err) {
        console.error("Error marking messages as read:", err);
      }
    },
    [transportId, resolvedOrderId, onMessagesRead],
  );

  const fetchMessages = useCallback(
    async (orderIdToUse) => {
      const cleanTransportId = cleanId(transportId);
      const cleanOrderId = cleanId(orderIdToUse || resolvedOrderId);
      if (!cleanTransportId || !cleanOrderId) return;

      if (isInitialLoadRef.current) setLoading(true);
      setError("");

      try {
        const result = await driverAPI.transportOrderChat.getMessages(
          cleanTransportId,
          cleanOrderId,
        );
        if (!isMountedRef.current) return;

        const isEnabled =
          result?.data?.data?.statistics?.is_chat_enabled ??
          result?.data?.statistics?.is_chat_enabled;
        setIsChatEnabled(isEnabled === undefined ? true : Boolean(isEnabled));

        if (result.success) {
          let messagesData = [];
          if (Array.isArray(result.data)) {
            messagesData = result.data;
          } else if (Array.isArray(result.data?.messages)) {
            messagesData = result.data.messages;
          } else if (Array.isArray(result.data?.data?.messages)) {
            messagesData = result.data.data.messages;
          }

          messagesData.sort((a, b) => a.id - b.id);
          setMessages(messagesData);
          messagesData.forEach((msg) => processedMessageIds.current.add(msg.id));

          if (messagesData.length > 0 && !currentUserRef.current) {
            const driverMessage = messagesData.find(
              (msg) =>
                msg.sender?.role === "Driver" || msg.sender_type === "driver",
            );
            if (driverMessage) {
              currentUserRef.current = driverMessage.sender;
              setCurrentUser(driverMessage.sender);
            }
          }

          if (show) markMessagesAsRead(cleanOrderId);
        } else {
          setError(result.message || "Failed to load messages");
        }
      } catch (err) {
        if (!isMountedRef.current) return;
        console.error("Error fetching messages:", err);
        setError("Network error occurred");
      }

      if (!isMountedRef.current) return;
      if (isInitialLoadRef.current) {
        setLoading(false);
        isInitialLoadRef.current = false;
      }
    },
    [transportId, resolvedOrderId, show, markMessagesAsRead],
  );

  // Pusher
  useEffect(() => {
    if (!show || !resolvedOrderId) return;

    const cleanOrderId = cleanId(resolvedOrderId);
    if (!cleanOrderId) return;

    if (pusherRef.current) {
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    const pusher = new Pusher(PUSHER_KEY, {
      cluster: "mt1",
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
    });
    pusherRef.current = pusher;

    const channel = pusher.subscribe(`chat.transport_order.${cleanOrderId}`);

    const handleNewMessage = (data) => {
      const hasCompleteAttachments =
        data.attachments?.length > 0 && data.attachments[0]?.file_url;

      if (processedMessageIds.current.has(data.id)) {
        if (hasCompleteAttachments && isMountedRef.current) {
          setMessages((prev) =>
            prev.map((msg) => (msg.id === data.id ? { ...msg, ...data } : msg)),
          );
          scrollToBottom();
        }
        return;
      }

      processedMessageIds.current.add(data.id);
      if (!isMountedRef.current) return;

      if (data.has_attachments && !hasCompleteAttachments) {
        setTimeout(() => fetchMessages(cleanOrderId), 300);
        return;
      }

      setMessages((prev) => {
        if (prev.some((msg) => msg.id === data.id)) {
          return prev.map((msg) =>
            msg.id === data.id ? { ...msg, ...data } : msg,
          );
        }
        return [
          ...prev,
          {
            ...data,
            is_read: data.sender?.id === currentUserRef.current?.id,
          },
        ].sort((a, b) => a.id - b.id);
      });

      scrollToBottom();
      if (data.sender?.id !== currentUserRef.current?.id) {
        markMessagesAsRead(cleanOrderId);
      }
    };

    channel.bind("message.transport.sent", handleNewMessage);

    return () => {
      if (pusherRef.current) {
        pusherRef.current.disconnect();
        pusherRef.current = null;
      }
    };
  }, [show, resolvedOrderId, fetchMessages, markMessagesAsRead]);

  // فتح المودال: جيب order موجود فقط (من غير إنشاء)
  useEffect(() => {
    if (!show || !transportId) return;

    let cancelled = false;

    const resolveOrderOnOpen = async () => {
      if (initialOrderId) {
        setResolvedOrderId(initialOrderId);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const response = await driverAPI.transports.getMyOrder(
          cleanId(transportId),
        );

        if (cancelled || !isMountedRef.current) return;

        const order = response?.data?.data ?? response?.data ?? null;
        const orderId = order?.id || null;
        setResolvedOrderId(orderId);

        if (!orderId) setLoading(false);
      } catch (err) {
        if (cancelled || !isMountedRef.current) return;
        console.error("Error resolving order on open:", err);
        setResolvedOrderId(null);
        setLoading(false);
      }
    };

    resolveOrderOnOpen();
    return () => {
      cancelled = true;
    };
  }, [show, transportId, initialOrderId]);

  // جلب الرسائل لما يبقى فيه orderId
  useEffect(() => {
    if (show && transportId && resolvedOrderId) {
      isInitialLoadRef.current = true;
      fetchMessages(resolvedOrderId);
    } else if (show && transportId && !resolvedOrderId) {
      setMessages([]);
      setLoading(false);
      isInitialLoadRef.current = false;
    } else if (!show) {
      setMessages([]);
      setCurrentUser(null);
      currentUserRef.current = null;
      setResolvedOrderId(null);
      setIsChatEnabled(true);
      setAttachments((prev) => {
        prev.forEach((a) => {
          if (a.preview) URL.revokeObjectURL(a.preview);
        });
        return [];
      });
      processedMessageIds.current.clear();
      isInitialLoadRef.current = true;
    }
  }, [show, transportId, resolvedOrderId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
      if (externalUnreadCount !== undefined) {
        setUnreadCount(externalUnreadCount);
      }
    }, [externalUnreadCount]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const next = files.map((file) => {
      const isImage = file.type.startsWith("image/");
      return {
        id: Math.random().toString(36).slice(2, 11),
        file,
        filename: file.name,
        file_size: file.size,
        file_size_formatted: formatFileSize(file.size),
        is_image: isImage,
        preview: isImage ? URL.createObjectURL(file) : null,
      };
    });

    setAttachments((prev) => [...prev, ...next]);
    e.target.value = "";
  };

  const removeAttachment = (attachmentId) => {
    setAttachments((prev) => {
      const found = prev.find((a) => a.id === attachmentId);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return prev.filter((a) => a.id !== attachmentId);
    });
  };

  const handleSend = async () => {
    if ((newMessage.trim() === "" && attachments.length === 0) || !transportId) {
      return;
    }

    setSending(true);
    setError("");

    try {
      const orderIdToUse = await ensureOrderId();
      const cleanTransportId = cleanId(transportId);
      if (!cleanTransportId || !orderIdToUse) {
        throw new Error("Missing transport or order ID");
      }

      const formData = new FormData();
      formData.append("message", newMessage.trim());
      attachments.forEach((attachment) => {
        if (attachment.file) {
          formData.append("attachments[]", attachment.file, attachment.filename);
        }
      });

      const result = await driverAPI.transportOrderChat.sendMessage(
        cleanTransportId,
        orderIdToUse,
        formData,
      );

      if (!isMountedRef.current) return;

      if (result.success) {
        setNewMessage("");
        setAttachments((prev) => {
          prev.forEach((a) => {
            if (a.preview) URL.revokeObjectURL(a.preview);
          });
          return [];
        });
        setTimeout(() => fetchMessages(orderIdToUse), 400);
        scrollToBottom();
      } else {
        setError(result.message || "Failed to send message");
      }
    } catch (err) {
      if (!isMountedRef.current) return;
      console.error("Error sending message:", err);
      setError(err.message || "Network error occurred");
    }

    if (isMountedRef.current) setSending(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleModalClose = () => {
    setMessages([]);
    setNewMessage("");
    setError("");
    setCurrentUser(null);
    currentUserRef.current = null;
    setResolvedOrderId(null);
    setIsChatEnabled(true);
    setAttachments((prev) => {
      prev.forEach((a) => {
        if (a.preview) URL.revokeObjectURL(a.preview);
      });
      return [];
    });
    processedMessageIds.current.clear();
    isInitialLoadRef.current = true;

    if (pusherRef.current) {
      pusherRef.current.disconnect();
      pusherRef.current = null;
    }

    handleClose();
  };

  const isCurrentUser = (message) =>
    currentUser && message.sender?.id === currentUser.id;

  const AttachmentPreview = ({ attachment, onRemove }) => (
    <span className="chat-attachment-item">
      {attachment.is_image && (attachment.preview || attachment.file_url) ? (
        <img
          src={attachment.preview || attachment.file_url}
          alt={attachment.filename}
          style={{ width: 28, height: 28, objectFit: "cover", borderRadius: 4 }}
        />
      ) : (
        <FaFile size={12} />
      )}
      <span className="text-truncate" style={{ maxWidth: 100 }}>
        {attachment.filename}
      </span>
      {onRemove && (
        <button className="remove-btn" onClick={() => onRemove(attachment.id)}>
          <FaTimes size={10} />
        </button>
      )}
    </span>
  );

  const MessageAttachments = ({ attachments: msgAttachments }) => {
    if (!msgAttachments?.length) return null;
    return (
      <div>
        {msgAttachments.map((attachment) => {
          const isImage =
            attachment.is_image ||
            attachment.mime_type?.startsWith("image/") ||
            /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(
              attachment.file_url || "",
            );

          return (
            <div key={attachment.id} className="mb-1">
              {isImage && attachment.file_url ? (
                <a href={attachment.file_url} target="_blank" rel="noreferrer">
                  <img
                    src={attachment.file_url}
                    alt={attachment.filename || "image"}
                    className="chat-bubble-image"
                  />
                </a>
              ) : (
                <a
                  href={attachment.file_url}
                  target="_blank"
                  rel="noreferrer"
                  className="chat-file-attachment"
                >
                  <FaFile size={14} />
                  <span className="text-truncate" style={{ maxWidth: 140 }}>
                    {attachment.filename}
                  </span>
                </a>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="fw-bold">
          🚚 {t("general.order_chat")} #{transportId}
          {unreadCount > 0 && (
            <Badge bg="danger" className="ms-2">
              {unreadCount} {t("general.unread")}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-0">
        <div className="modal-chat-container" style={{ borderRadius: 0 }}>
          <div className="chat-header" style={{ borderRadius: 0 }}>
            <div className="chat-header-info">
              <div className="chat-header-avatar">
                {currentUser?.image ? (
                  <img
                    src={currentUser.image}
                    alt="Driver"
                    className="img-fluid rounded-circle"
                  />
                ) : (
                  currentUser?.name
                    ? currentUser.name.charAt(0).toUpperCase()
                    : "C"
                )}
              </div>
              <div className="chat-header-text">
                <h6>
                  {t("general.transport_order")}{" "}
                  {resolvedOrderId ? `#${resolvedOrderId}` : ""}
                </h6>
                <small>{t("general.chat_with_customer")}</small>
              </div>
            </div>
          </div>

          <div className="modal-chat-body">
            {error && (
              <Alert variant="danger" className="py-2 mx-2 mb-2" style={{ fontSize: 13 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <div className="chat-empty-state">
                <Spinner animation="border" style={{ color: "#ff6600" }} />
                <p className="mt-2">{t("general.loading")}</p>
              </div>
            ) : messages.length === 0 ? (
              <div className="chat-empty-state">
                <div className="empty-icon">💬</div>
                <h6>{t("general.no_messages")}</h6>
              </div>
            ) : (
              messages.map((msg, idx) => {
                const isSent = isCurrentUser(msg);
                const offerData = parseOfferCardMessage(msg.message);
                const ratingData = parseRatingCardMessage(msg.message);

                return (
                  <div
                    key={msg.id || idx}
                    className={`chat-message-row ${isSent ? "sent" : "received"}`}
                  >
                    <div
                      className={`chat-bubble ${isSent ? "sent" : "received"}`}
                      style={
                        offerData
                          ? { background: "transparent", boxShadow: "none", padding: "4px 0" }
                          : undefined
                      }
                    >
                      {!isSent &&
                        (idx === 0 || isCurrentUser(messages[idx - 1])) && (
                          <div className="chat-bubble-sender">
                            {msg.sender?.name || t("general.unknown")}
                          </div>
                        )}

                      <MessageAttachments attachments={msg.attachments} />

                      {/* {offerData ? (
                        <OfferMessageCard
                          data={{
                            ...offerData,
                            status: resolveOfferStatus(offerData),
                          }}
                        />
                      ) : (
                        msg.message && (
                          <div className="chat-bubble-text">{msg.message}</div>
                        )
                      )} */}

                      {offerData ? (
                        <OfferMessageCard data={{ ...offerData, status: resolveOfferStatus(offerData) }} />
                      ) : ratingData ? (
                        <RatingMessageCard data={ratingData} />
                      ) : (
                        msg.message && <div className="chat-bubble-text">{msg.message}</div>
                      )}

                      <div className="chat-bubble-meta">
                        <span>
                          {msg.created_at
                            ? (() => {
                                const d = parseDate(msg.created_at);
                                return d
                                  ? d.toLocaleTimeString([], {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : msg.created_at;
                              })()
                            : ""}
                        </span>
                        {isSent && (
                          <span
                            className={`chat-tick ${msg.is_read ? "read" : "sent-tick"}`}
                          >
                            {msg.is_read ? "✓✓" : "✓"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {attachments.length > 0 && (
            <div className="chat-attachment-preview" style={{ margin: 0, borderRadius: 0 }}>
              {attachments.map((attachment) => (
                <AttachmentPreview
                  key={attachment.id}
                  attachment={attachment}
                  onRemove={removeAttachment}
                />
              ))}
            </div>
          )}

          {!isChatEnabled ? (
            <div className="chat-disabled-overlay p-3 text-center">
              <FaLock size={25} className="text-muted mb-3" />
              <h6 className="text-muted">
                {t("clientTable.orderDetails.chat_is_disabled")}
              </h6>
            </div>
          ) : (
            <>
              <div className="modal-chat-input">
                <button
                  className="chat-attach-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                  type="button"
                >
                  <FaPaperclip size={18} />
                </button>
                <Form.Control
                  className="chat-text-input"
                  placeholder={t("general.type_message")}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                  as="textarea"
                  rows={1}
                  style={{ resize: "none" }}
                />
                <button
                  className="chat-send-btn"
                  onClick={handleSend}
                  disabled={
                    sending ||
                    (newMessage.trim() === "" && attachments.length === 0)
                  }
                  type="button"
                >
                  {sending ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <FaPaperPlane size={16} />
                  )}
                </button>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
              />
            </>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleModalClose} className="rounded-pill px-3">
          {t("general.back")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}